import React, { useEffect, useState } from 'react';
import { Select, Input, Button, Divider, Space, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../variables/api/axiosInstance';
import { filtreSansAccents } from '../utils/rechercheTexte';

/* Code-friendly slug : "Chauffeur Routier" → "CHAUFFEUR_ROUTIER" */
const toCode = (s) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/**
 * Select AntD piloté par l'API `/v1/parametres` (CBConnect), avec ajout
 * d'une nouvelle entrée à la volée.
 *
 *   <SelectWithAdd
 *     value={value}
 *     onChange={setValue}
 *     categorie="PROFESSION"        // Liste servie depuis /v1/parametres/categorie/PROFESSION
 *     placeholder="Sélectionner une profession"
 *     addLabel="Ajouter une profession"
 *     allowCreate                   // autorise le bouton "Ajouter"
 *   />
 *
 * Pour un endpoint custom (hors paramètres) :
 *
 *   <SelectWithAdd
 *     fetchUrl="/v1/xxx"
 *     createUrl="/v1/xxx"
 *     valueKey="id"
 *     labelKey="nom"
 *     buildCreatePayload={(label) => ({ nom: label })}
 *   />
 */
export default function SelectWithAdd({
  value, onChange,

  // Mode "paramétrage CBConnect"
  categorie = null,

  // Mode custom (surcharge si fourni)
  fetchUrl: fetchUrlProp = null,
  createUrl: createUrlProp = null,
  valueKey: valueKeyProp = null,
  labelKey: labelKeyProp = null,
  buildCreatePayload: buildCreatePayloadProp = null,

  allowCreate = true,
  placeholder,
  addLabel,
  allowClear = true,
  style,
  size = 'middle',
  disabled = false,
}) {
  const { t } = useTranslation('commun');
  // Résolution des URLs / keys selon le mode
  const fetchUrl = fetchUrlProp ?? (categorie ? `/v1/parametres/categorie/${categorie}` : null);
  const createUrl = createUrlProp ?? (categorie ? '/v1/parametres' : null);
  const valueKey = valueKeyProp ?? 'cle';
  const labelKey = labelKeyProp ?? 'valeur';
  const buildCreatePayload = buildCreatePayloadProp ?? ((label) =>
    categorie
      ? { typeParametre: 'LISTE', cle: `${categorie}.${toCode(label)}`, valeur: label }
      : { [labelKey]: label }
  );
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [adding, setAdding] = useState(false);

  const load = async () => {
    if (!fetchUrl) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(fetchUrl);
      const raw = res.data?.data ?? res.data ?? [];
      setOptions(Array.isArray(raw) ? raw : []);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fetchUrl]);

  const onAdd = async (e) => {
    e?.preventDefault();
    const libelle = newItem.trim();
    if (!libelle) return;
    if (!createUrl) {
      toast.error(t('selectWithAdd.ajoutNonAutorise'));
      return;
    }
    setAdding(true);
    try {
      const res = await axiosInstance.post(createUrl, buildCreatePayload(libelle));
      const created = res.data?.data ?? res.data;
      if (created) {
        setOptions((prev) => [...prev, created]);
        onChange?.(created[valueKey]);
        toast.success(t('selectWithAdd.ajoute', { libelle }));
        setNewItem('');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || t('selectWithAdd.echecAjout'));
    } finally {
      setAdding(false);
    }
  };

  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder={placeholder === undefined ? t('selectWithAdd.selectionner') : placeholder}
      allowClear={allowClear}
      showSearch
      filterOption={filtreSansAccents}
      optionFilterProp="label"
      style={style}
      size={size}
      disabled={disabled}
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : t('selectWithAdd.aucunElement')}
      options={options.map((o) => ({
        value: o[valueKey],
        label: o[labelKey],
      }))}
      dropdownRender={(menu) => (
        <>
          {menu}
          {createUrl && (
            <>
              <Divider style={{ margin: '6px 0' }} />
              <Space.Compact style={{ padding: 6, width: '100%' }}>
                <Input
                  size="small"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onPressEnter={onAdd}
                  placeholder={addLabel === undefined ? t('selectWithAdd.ajouter') : addLabel}
                  disabled={adding}
                />
                <Button
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={onAdd}
                  loading={adding}
                  disabled={!newItem.trim()}
                  style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
                >
                  {t('selectWithAdd.ajouter')}
                </Button>
              </Space.Compact>
            </>
          )}
        </>
      )}
    />
  );
}
